import React from "react";
import { Form, Formik } from "formik";
import * as Yup from "yup";
import FormField from "components/v2/FormField";
import { AiOutlineLoading3Quarters } from "react-icons/ai";
import useChangePassword from "api/UserResource/useChangePassword";

const ChangePassword = () => {
	const initialValues = {
		current_password: "",
		password: "",
		password_confirmation: "",
	};

	const updateSchema = Yup.object().shape({
		current_password: Yup.string().required("Current password is required"),
		password: Yup.string()
			.min(6, "At least 6 characters required")
			.matches(/[a-zA-Z]/, "Must have at least one alphabet character")
			.matches(/[0-9]/, "Must have at least one digit")
			.required("Password required"),
		password_confirmation: Yup.string()
			.oneOf([Yup.ref("password")], "Passwords didn't match")
			.required("Password confirmation required"),
	});

	const { mutateAsync, isLoading } = useChangePassword();

	const handleSave = async (data) => {
		await mutateAsync(data);
	};

	return (
		<>
			<div className="space-y-6">
				{/* Security Information Card */}
				<div className="p-6 rounded-xl border border-green-200 bg-green-50">
					<div className="flex items-start gap-4">
						<div className="flex-shrink-0 w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
							<svg
								className="w-6 h-6 text-green-600"
								fill="none"
								stroke="currentColor"
								viewBox="0 0 24 24"
							>
								<path
									strokeLinecap="round"
									strokeLinejoin="round"
									strokeWidth={2}
									d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"
								/>
							</svg>
						</div>
						<div>
							<h4 className="font-medium text-green-900 mb-2">Password Security Tips</h4>
							<p className="text-sm text-green-700 mb-3">
								Your password is used for logging in along with your email/username. Make it
								difficult to guess but easy to remember for yourself.
							</p>
							<ul className="text-sm text-green-700 space-y-1">
								<li>• Use at least 6 characters</li>
								<li>• Include both letters and numbers</li>
								<li>• Avoid common words or personal information</li>
								<li>• Consider using a password manager</li>
							</ul>
						</div>
					</div>
				</div>

				<Formik
					initialValues={initialValues}
					validationSchema={updateSchema}
					onSubmit={(data) => handleSave(data)}
				>
					<Form className="space-y-6">
						{/* Current Password */}
						<div className="space-y-2">
							<FormField
								type="password"
								name="current_password"
								label="Current Password *"
								// placeholder="Enter your current password"
								classNames={{
									label: "text-sm font-medium text-gray-700 mb-1 block",
									input:
										"w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl focus:ring-2 focus:ring-akgreen focus:border-transparent transition-all duration-200",
								}}
							/>
						</div>

						{/* New Password */}
						<div className="space-y-2">
							<FormField
								type="password"
								name="password"
								label="New Password *"
								// placeholder="Enter your new password"
								classNames={{
									label: "text-sm font-medium text-gray-700 mb-1 block",
									input:
										"w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl focus:ring-2 focus:ring-akgreen focus:border-transparent transition-all duration-200",
								}}
							/>
						</div>

						{/* Confirm New Password */}
						<div className="space-y-2">
							<FormField
								type="password"
								name="password_confirmation"
								label="Confirm New Password *"
								// placeholder="Confirm your new password"
								classNames={{
									label: "text-sm font-medium text-gray-700 mb-1 block",
									input:
										"w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl focus:ring-2 focus:ring-akgreen focus:border-transparent transition-all duration-200",
								}}
							/>
						</div>

						{/* Submit Button */}
						<div className="pt-6 border-t border-gray-100">
							<button
								type="submit"
								disabled={isLoading}
								className="w-full sm:w-auto px-8 py-3 bg-gradient-to-r from-akgreen to-akgreener text-white font-medium rounded-xl hover:from-akgreener hover:to-akgreen focus:outline-none focus:ring-2 focus:ring-akgreen focus:ring-offset-2 transition-all duration-200 transform hover:scale-[1.02] disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none flex items-center justify-center gap-2 min-w-[160px]"
							>
								{isLoading ? (
									<>
										<AiOutlineLoading3Quarters size={20} className="animate-spin" />
										<span>Updating...</span>
									</>
								) : (
									<span>Update Password</span>
								)}
							</button>
						</div>
					</Form>
				</Formik>
			</div>
		</>
	);
};

export default ChangePassword;
